import { useEffect, useRef, useState } from "react";
import { Palette, X } from "@phosphor-icons/react";

/**
 * LogoColorPicker — extract 6 dominant colors from any image + let the admin
 * click one to seed the landlord's brand_color.
 *
 * Uses the browser Canvas API only — zero dependencies, zero backend hits.
 * The image is loaded with crossOrigin="anonymous" so same-origin/CORS-open
 * logos work; if the load fails we surface a clear message.
 */
function hex(n) { return n.toString(16).padStart(2, "0"); }
function rgbToHex(r, g, b) { return `#${hex(r)}${hex(g)}${hex(b)}`; }

/**
 * Bucket pixels into a coarse 16-per-channel color histogram, then return
 * the top-N most common colors. Skips near-white / near-black so the
 * palette matches the *interesting* parts of the logo, not the background.
 */
function extractPalette(imgEl, want = 6) {
  const c = document.createElement("canvas");
  const W = 96, H = 96;
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.drawImage(imgEl, 0, 0, W, H);
  let data;
  try { data = ctx.getImageData(0, 0, W, H).data; }
  catch { return []; }

  const buckets = new Map();
  const step = 4;               // sample every 4th pixel for speed
  for (let i = 0; i < data.length; i += 4 * step) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 200) continue;                                   // skip transparent
    // Skip near-white and near-black — those hog the histogram on most logos.
    const min = Math.min(r, g, b), max = Math.max(r, g, b);
    if (max < 25) continue;
    if (min > 235) continue;
    const bucketR = r >> 4, bucketG = g >> 4, bucketB = b >> 4;
    const key = (bucketR << 8) | (bucketG << 4) | bucketB;
    const prev = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
    prev.r += r; prev.g += g; prev.b += b; prev.n += 1;
    buckets.set(key, prev);
  }
  const sorted = Array.from(buckets.values()).sort((a, b) => b.n - a.n);
  const seen = new Set();
  const out = [];
  for (const p of sorted) {
    const r = Math.round(p.r / p.n), g = Math.round(p.g / p.n), b = Math.round(p.b / p.n);
    const hexVal = rgbToHex(r, g, b);
    // Dedupe near-identical colors so we don't return 6 shades of the same blue.
    const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(hexVal);
    if (out.length >= want) break;
  }
  return out;
}

export default function LogoColorPicker({ src, onPick }) {
  const [open, setOpen] = useState(false);
  const [palette, setPalette] = useState([]);
  const [error, setError] = useState("");
  const imgRef = useRef(null);

  const load = () => {
    setError("");
    setPalette([]);
    if (!src) { setError("Upload a logo first"); return; }
    setOpen(true);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const colors = extractPalette(img);
        if (!colors.length) setError("Couldn't find any strong colors — try a different logo.");
        else setPalette(colors);
      } catch (e) {
        setError("Browser blocked the canvas read (cross-origin). Try re-uploading the logo directly.");
      }
    };
    img.onerror = () => setError("Couldn't load logo image.");
    img.src = src;
    imgRef.current = img;
  };

  return (
    <>
      <button
        type="button"
        onClick={load}
        data-testid="logo-color-picker-btn"
        disabled={!src}
        title={src ? "Pull dominant colors from this logo" : "Upload a logo first"}
        className="h-11 px-3 border-2 border-[#0f172a] bg-amber-300 text-slate-900 hover:bg-amber-400 font-black uppercase text-[10px] tracking-widest disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
        <Palette weight="fill" size={14}/> Pick from Logo
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)} data-testid="logo-color-picker-modal">
          <div className="bg-white border-2 border-[#0f172a] shadow-[6px_6px_0_#0f172a] p-5 max-w-md w-full"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Palette weight="fill" size={20}/>
              <div className="font-display font-black text-lg uppercase tracking-wide flex-1">Pick a brand color</div>
              <button type="button" onClick={() => setOpen(false)} className="h-8 w-8 border-2 border-slate-300 hover:bg-slate-100" aria-label="Close">
                <X weight="bold" size={14} className="mx-auto"/>
              </button>
            </div>
            {error && (
              <div className="p-2 border-2 border-red-400 bg-red-50 text-xs text-red-800">{error}</div>
            )}
            {!error && palette.length === 0 && (
              <div className="py-6 text-center text-xs text-slate-500">Scanning logo…</div>
            )}
            {palette.length > 0 && (
              <>
                <p className="text-xs text-slate-500 mb-3">Tap a swatch to apply it as this landlord's brand color. It'll show up on the tenant reporting page banner + welcome card.</p>
                <div className="grid grid-cols-3 gap-3">
                  {palette.map((c, i) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { onPick(c); setOpen(false); }}
                      data-testid={`logo-color-swatch-${i}`}
                      className="group border-2 border-[#0f172a] hover:scale-105 active:scale-95 transition-transform shadow-[2px_2px_0_#0f172a]">
                      <div className="h-16" style={{ background: c }}/>
                      <div className="p-1 text-center font-mono text-[10px] font-black tracking-wider">{c.toUpperCase()}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
